<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link to="/admin/agents">Agents</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          {{ agent?.name || "Details" }}
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading agent details...</p>
      </div>

      <!-- Agent Details -->
      <div v-else-if="agent">
        <!-- Summary Card -->
        <div class="row mb-4">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h4 class="mb-2">
                      {{ agent.name }}
                      <span
                        :class="[
                          'badge',
                          'ms-2',
                          agent.is_active ?? agent.isActive
                            ? 'bg-success'
                            : 'bg-secondary',
                        ]"
                      >
                        {{
                          agent.is_active ?? agent.isActive
                            ? "Active"
                            : "Inactive"
                        }}
                      </span>
                    </h4>
                    <p class="text-muted mb-2">
                      <code class="text-primary">{{ agent.slug }}</code>
                    </p>
                    <p class="mb-3">{{ agent.description }}</p>
                    <div class="d-flex gap-3 flex-wrap">
                      <span>
                        <i class="fa fa-microchip me-1 text-primary"></i>
                        <strong>Model:</strong> {{ agent.model }}
                      </span>
                      <span>
                        <i class="fa fa-thermometer-half me-1 text-warning"></i>
                        <strong>Temperature:</strong> {{ agent.temperature }}
                      </span>
                      <span>
                        <i class="fa fa-coins me-1 text-info"></i>
                        <strong>Max Tokens:</strong>
                        {{ agent.max_output_tokens || agent.maxOutputTokens }}
                      </span>
                      <span>
                        <i class="fa fa-wrench me-1 text-success"></i>
                        <strong>Tools:</strong>
                        {{
                          getToolsCount(
                            agent.tools_allowed || agent.toolsAllowed
                          )
                        }}
                      </span>
                      <span>
                        <i class="fa fa-database me-1 text-warning"></i>
                        <strong>Knowledge Bases:</strong>
                        {{
                          getKnowledgeBasesCount(
                            agent.knowledge_base_ids || agent.knowledgeBaseIds
                          )
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <router-link
                      :to="`/admin/agents/${agentId}/edit`"
                      class="btn btn-primary btn-sm mb-2"
                    >
                      <i class="fa fa-edit me-1"></i>
                      Edit Agent
                    </router-link>
                    <br />
                    <router-link
                      to="/admin/agents"
                      class="btn btn-outline-secondary btn-sm"
                    >
                      <i class="fa fa-arrow-left me-1"></i>
                      Back to List
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <ul class="nav nav-tabs card-header-tabs" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button
                      class="nav-link"
                      :class="{ active: activeTab === 'assignments' }"
                      @click="activeTab = 'assignments'"
                      type="button"
                    >
                      <i class="fa fa-users me-1"></i>
                      Assignments
                      <span
                        v-if="assignments.length > 0"
                        class="badge bg-primary ms-1"
                      >
                        {{ assignments.length }}
                      </span>
                    </button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button
                      class="nav-link"
                      :class="{ active: activeTab === 'test-chat' }"
                      @click="activeTab = 'test-chat'"
                      type="button"
                    >
                      <i class="fa fa-comment me-1"></i>
                      Preview / Test Chat
                    </button>
                  </li>
                </ul>
              </div>

              <div class="card-body">
                <!-- Assignments Tab -->
                <div v-if="activeTab === 'assignments'">
                  <!-- Add Assignment Form -->
                  <div class="card bg-light mb-4">
                    <div class="card-body">
                      <h6 class="mb-3">
                        <i class="fa fa-plus-circle me-2"></i>Add Assignment
                      </h6>
                      <form @submit.prevent="handleAddAssignment">
                        <div class="row">
                          <div class="col-md-4 mb-3">
                            <label for="scopeType" class="form-label text-black">
                              Scope Type <span class="text-danger">*</span>
                            </label>
                            <select
                              class="form-select"
                              id="scopeType"
                              v-model="assignmentForm.scopeType"
                              required
                            >
                              <option value="">Select Type</option>
                              <option value="MODULE">Module</option>
                              <option value="ROLE">Role</option>
                              <option value="USER">User</option>
                            </select>
                          </div>

                          <div
                            v-if="assignmentForm.scopeType === 'USER'"
                            class="col-md-4 mb-3"
                          >
                            <label for="userSearch" class="form-label text-black">
                              Search User <span class="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              class="form-control"
                              id="userSearch"
                              v-model="userSearchQuery"
                              @input="handleUserSearch"
                              placeholder="Search by name or email..."
                            />
                            <!-- User Dropdown -->
                            <div
                              v-if="userSearchResults.length > 0"
                              class="list-group mt-2"
                              style="max-height: 200px; overflow-y: auto"
                            >
                              <button
                                v-for="user in userSearchResults"
                                :key="user._id || user.id"
                                type="button"
                                class="list-group-item list-group-item-action"
                                @click="selectUser(user)"
                              >
                                {{ user.name }} ({{ user.email }})
                              </button>
                            </div>
                            <div
                              v-if="assignmentForm.selectedUser"
                              class="mt-2"
                            >
                              <span class="badge bg-info">
                                Selected: {{ assignmentForm.selectedUser.name }}
                              </span>
                            </div>
                          </div>

                          <div
                            v-else-if="assignmentForm.scopeType"
                            class="col-md-4 mb-3"
                          >
                            <label for="scopeId" class="form-label text-black">
                              {{
                                assignmentForm.scopeType === "MODULE"
                                  ? "Module Key"
                                  : "Role Name"
                              }}
                              <span class="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              class="form-control"
                              id="scopeId"
                              v-model="assignmentForm.scopeId"
                              :placeholder="
                                assignmentForm.scopeType === 'MODULE'
                                  ? 'e.g., customer-service'
                                  : 'e.g., admin'
                              "
                              required
                            />
                          </div>

                          <div class="col-md-4 mb-3 d-flex align-items-end">
                            <button
                              type="submit"
                              class="btn btn-primary w-100"
                              :disabled="isAddingAssignment"
                            >
                              <span v-if="isAddingAssignment">
                                <span
                                  class="spinner-border spinner-border-sm me-1"
                                  role="status"
                                ></span>
                                Adding...
                              </span>
                              <span v-else>
                                <i class="fa fa-plus me-1"></i>
                                Add Assignment
                              </span>
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>

                  <!-- Assignments List -->
                  <div v-if="isLoadingAssignments" class="text-center py-4">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>

                  <div
                    v-else-if="assignments.length === 0"
                    class="text-center py-4"
                  >
                    <i class="fa fa-info-circle fa-2x text-muted mb-2"></i>
                    <p class="text-muted">No assignments yet</p>
                  </div>

                  <div v-else class="table-responsive">
                    <table class="table table-hover">
                      <thead class="table-dark">
                        <tr>
                          <th>Scope Type</th>
                          <th>Scope ID</th>
                          <th>Name</th>
                          <th>Created</th>
                          <th class="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="assignment in assignments"
                          :key="assignment._id || assignment.id"
                        >
                          <td>
                            <span
                              :class="[
                                'badge',
                                getScopeTypeBadgeClass(assignment.scopeType),
                              ]"
                            >
                              {{ assignment.scope_type }}
                            </span>
                          </td>
                          <td>
                            <code>{{ assignment.scope_id }}</code>
                          </td>
                          <td>
                            <span v-if="assignment.agent_id?.name">
                              {{ assignment.agent_id?.name }}
                            </span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td>
                            <small>{{
                              formatDate(assignment.created_at)
                            }}</small>
                          </td>
                          <td class="text-end">
                            <button
                              @click="confirmDeleteAssignment(assignment)"
                              class="btn btn-outline-danger btn-sm"
                              title="Delete"
                            >
                              <i class="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Test Chat Tab -->
                <div v-if="activeTab === 'test-chat'">
                  <div class="row">
                    <div class="col-12">
                      <!-- Chat Messages -->
                      <div class="chat-container mb-3" ref="chatContainerRef">
                        <div
                          v-if="chatMessages.length === 0"
                          class="text-center py-5 text-muted"
                        >
                          <i class="fa fa-comments fa-3x mb-3"></i>
                          <p>Start a conversation to test the agent</p>
                        </div>
                        <div
                          v-for="(message, index) in chatMessages"
                          :key="index"
                          :class="[
                            'chat-message',
                            message.role === 'user'
                              ? 'user-message'
                              : 'agent-message',
                          ]"
                        >
                          <div class="message-header">
                            <strong>{{
                              message.role === "user" ? "You" : agent.name
                            }}</strong>
                            <small class="text-muted ms-2">{{
                              formatTime(message.timestamp)
                            }}</small>
                          </div>
                          <div class="message-content">
                            {{ message.content }}
                          </div>
                        </div>
                        <div
                          v-if="isSendingMessage"
                          class="chat-message agent-message"
                        >
                          <div class="message-header">
                            <strong>{{ agent.name }}</strong>
                          </div>
                          <div class="message-content">
                            <span
                              class="spinner-border spinner-border-sm me-2"
                            ></span>
                            Thinking...
                          </div>
                        </div>
                      </div>

                      <!-- Chat Input -->
                      <form @submit.prevent="handleSendMessage">
                        <div class="input-group">
                          <input
                            type="text"
                            class="form-control"
                            v-model="chatInput"
                            placeholder="Type your message..."
                            :disabled="isSendingMessage"
                          />
                          <button
                            class="btn btn-primary"
                            type="submit"
                            :disabled="!chatInput.trim() || isSendingMessage"
                          >
                            <i class="fa fa-paper-plane me-1"></i>
                            Send
                          </button>
                           <button
                          @click="clearChat"
                          class="btn btn-secondary btn-sm ms-2"
                          :disabled="chatMessages.length === 0"
                        >
                          <i class="fa fa-trash me-1"></i>
                          Clear Chat
                        </button>
                        </div>
                      </form>

                       
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="text-center py-5">
        <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
        <p class="text-danger">Failed to load agent details</p>
        <router-link to="/admin/agents" class="btn btn-primary">
          Back to Agents
        </router-link>
      </div>
    </div>

    <!-- Delete Assignment Confirmation Modal -->
    <div
      class="modal fade"
      id="deleteAssignmentModal"
      tabindex="-1"
      ref="deleteAssignmentModalRef"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this assignment?</p>
            <p class="text-muted">
              <strong>Scope Type:</strong> {{ assignmentToDelete?.scopeType
              }}<br />
              <strong>Scope ID:</strong> {{ assignmentToDelete?.scopeId }}
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteAssignment"
              :disabled="isDeletingAssignment"
            >
              <span v-if="isDeletingAssignment">
                <span
                  class="spinner-border spinner-border-sm me-1"
                  role="status"
                ></span>
                Deleting...
              </span>
              <span v-else>
                <i class="fa fa-trash me-1"></i>
                Delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, onMounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import apiService from "@/services/apiService";
import { Modal } from "bootstrap";

// Router
const router = useRouter();
const route = useRoute();

// Computed
const agentId = computed(() => route.params.id as string);

// Data
const agent = ref<any>(null);
const isLoading = ref(false);
const activeTab = ref("assignments");

// Assignments
const assignments = ref<any[]>([]);
const isLoadingAssignments = ref(false);
const isAddingAssignment = ref(false);
const assignmentForm = reactive({
  scopeType: "",
  scopeId: "",
  selectedUser: null as any,
});

// User search
const userSearchQuery = ref("");
const userSearchResults = ref<any[]>([]);
const userSearchTimeout = ref<any>(null);

// Delete assignment
const assignmentToDelete = ref<any>(null);
const deleteAssignmentModalRef = ref<HTMLElement | null>(null);
const isDeletingAssignment = ref(false);

// Chat
const chatMessages = ref<any[]>([]);
const chatInput = ref("");
const isSendingMessage = ref(false);
const chatContainerRef = ref<HTMLElement | null>(null);

// Methods
const loadAgent = async () => {
  isLoading.value = true;
  try {
    const response = await apiService.getAgentById(agentId.value);
    agent.value = response;
  } catch (err: any) {
    console.error("Error loading agent:", err);
    showToast(err?.response?.data?.message || "Failed to load agent", "error");
  } finally {
    isLoading.value = false;
  }
};

const loadAssignments = async () => {
  isLoadingAssignments.value = true;
  try {
    const response = await apiService.getAgentAssignments(agentId.value);
    assignments.value = response.data || response.assignments || [];
  } catch (err: any) {
    console.error("Error loading assignments:", err);
    showToast(
      err?.response?.data?.message || "Failed to load assignments",
      "error"
    );
  } finally {
    isLoadingAssignments.value = false;
  }
};

const handleUserSearch = () => {
  if (userSearchTimeout.value) {
    clearTimeout(userSearchTimeout.value);
  }

  userSearchTimeout.value = setTimeout(async () => {
    if (userSearchQuery.value.trim().length < 2) {
      userSearchResults.value = [];
      return;
    }

    try {
      const response = await apiService.searchUsers(userSearchQuery.value);
      userSearchResults.value = response.data || response.users || [];
    } catch (err: any) {
      console.error("Error searching users:", err);
      userSearchResults.value = [];
    }
  }, 300);
};

const selectUser = (user: any) => {
  assignmentForm.selectedUser = user;
  assignmentForm.scopeId = user._id || user.id;
  userSearchQuery.value = user.name;
  userSearchResults.value = [];
};

const handleAddAssignment = async () => {
  if (!assignmentForm.scopeType) {
    showToast("Please select a scope type", "error");
    return;
  }

  if (assignmentForm.scopeType === "USER" && !assignmentForm.selectedUser) {
    showToast("Please select a user", "error");
    return;
  }

  if (assignmentForm.scopeType !== "USER" && !assignmentForm.scopeId.trim()) {
    showToast("Please enter a scope ID", "error");
    return;
  }

  isAddingAssignment.value = true;
  try {
    const payload = {
      scopeType: assignmentForm.scopeType,
      scopeId:
        assignmentForm.scopeType === "USER"
          ? assignmentForm.selectedUser._id || assignmentForm.selectedUser.id
          : assignmentForm.scopeId.trim(),
    };

    await apiService.createAgentAssignment(agentId.value, payload);
    showToast("Assignment added successfully", "success");

    // Reset form
    assignmentForm.scopeType = "";
    assignmentForm.scopeId = "";
    assignmentForm.selectedUser = null;
    userSearchQuery.value = "";
    userSearchResults.value = [];

    // Reload assignments
    loadAssignments();
  } catch (err: any) {
    console.error("Error adding assignment:", err);
    showToast(
      err?.response?.data?.message || "Failed to add assignment",
      "error"
    );
  } finally {
    isAddingAssignment.value = false;
  }
};

const confirmDeleteAssignment = (assignment: any) => {
  assignmentToDelete.value = assignment;
  const modalElement = deleteAssignmentModalRef.value;
  if (modalElement) {
    const modal = new Modal(modalElement);
    modal.show();
  }
};

const deleteAssignment = async () => {
  if (!assignmentToDelete.value) return;

  isDeletingAssignment.value = true;
  try {
    await apiService.deleteAgentAssignment(
      agentId.value,
      assignmentToDelete.value._id || assignmentToDelete.value.id
    );
    showToast("Assignment deleted successfully", "success");

    // Close modal
    const modalElement = deleteAssignmentModalRef.value;
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    assignmentToDelete.value = null;
    loadAssignments();
  } catch (err: any) {
    console.error("Error deleting assignment:", err);
    showToast(
      err?.response?.data?.message || "Failed to delete assignment",
      "error"
    );
  } finally {
    isDeletingAssignment.value = false;
  }
};

const handleSendMessage = async () => {
  if (!chatInput.value.trim()) return;

  const userMessage = {
    role: "user",
    content: chatInput.value.trim(),
    timestamp: new Date(),
  };

  chatMessages.value.push(userMessage);
  const currentInput = chatInput.value;
  chatInput.value = "";

  // Scroll to bottom
  await nextTick();
  scrollToBottom();

  isSendingMessage.value = true;
  try {
    const response = await apiService.testAgentChat(
      agentId.value,
      currentInput,
      chatMessages.value
    );

    console.log("Chat response:", response);

    // Handle different response structures
    const assistantContent =
      response.data?.assistantMessage ||
      response.assistantMessage ||
      response.message ||
      response.response ||
      "No response";

    const agentMessage = {
      role: "agent",
      content: assistantContent,
      timestamp: new Date(),
    };

    chatMessages.value.push(agentMessage);

    await nextTick();
    scrollToBottom();
  } catch (err: any) {
    console.error("Error sending message:", err);
    showToast(
      err?.response?.data?.message || "Failed to send message",
      "error"
    );

    // Add error message
    chatMessages.value.push({
      role: "agent",
      content: "Sorry, I encountered an error processing your message.",
      timestamp: new Date(),
    });
  } finally {
    isSendingMessage.value = false;
  }
};

const clearChat = () => {
  if (confirm("Are you sure you want to clear the chat history?")) {
    chatMessages.value = [];
  }
};

const scrollToBottom = () => {
  if (chatContainerRef.value) {
    chatContainerRef.value.scrollTop = chatContainerRef.value.scrollHeight;
  }
};

const getScopeTypeBadgeClass = (scopeType: string): string => {
  switch (scopeType) {
    case "MODULE":
      return "bg-primary";
    case "ROLE":
      return "bg-success";
    case "USER":
      return "bg-info";
    default:
      return "bg-secondary";
  }
};

const getToolsCount = (tools: any): number => {
  if (!tools) return 0;
  if (Array.isArray(tools)) return tools.length;
  return 0;
};

const getKnowledgeBasesCount = (kbIds: any): number => {
  if (!kbIds) return 0;
  if (Array.isArray(kbIds)) return kbIds.length;
  return 0;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const formatTime = (timestamp: Date): string => {
  return timestamp.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const showToast = (message: string, type: "success" | "error" | "info") => {
  console.log(`[${type.toUpperCase()}] ${message}`);
  // Integrate with your toast library here
};

// Lifecycle
onMounted(() => {
  loadAgent();
  loadAssignments();
});
</script>

<style scoped>
.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef;
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  border-color: #0d6efd;
  background-color: transparent;
}

.chat-container {
  height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background-color: #f8f9fa;
}

.chat-message {
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 0.375rem;
  max-width: 80%;
}

.user-message {
  background-color: #0d6efd;
  color: white;
  margin-left: auto;
  text-align: right;
}

.agent-message {
  background-color: white;
  border: 1px solid #dee2e6;
}

.message-header {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.message-content {
  line-height: 1.5;
}

.user-message .message-header {
  color: rgba(255, 255, 255, 0.9);
}

.list-group-item {
  cursor: pointer;
}

code {
  font-size: 0.875rem;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
}
</style>
